import type { ApiMessage } from '../../api/types';
import type { GlobalState } from '../types';
import type { ApiChat } from '../../api/types';

import { isChatChannel, isChatSuperGroup } from '../helpers/chats';
import { selectTabState } from './tabs';
import { getCurrentTabId } from '../../util/establishMultitabRole';

export type FeedItem = {
  chatId: string;
  messageId: number;
  date: number;
};

const FEED_CHANNEL_LIMIT = 50;
const FEED_POST_AGE_DAYS = 7;
const FEED_POST_AGE_SECONDS = FEED_POST_AGE_DAYS * 24 * 60 * 60;

/** Returns channel IDs from the active chat list, capped at the most recently active. */
export function selectFeedChannelIds<T extends GlobalState>(global: T): string[] {
  const activeIds = global.chats.listIds.active || [];
  const chatsById = global.chats.byId;
  const lastMessageIds = global.chats.lastMessageIds.all || {};

  return activeIds
    .map((id) => chatsById[id])
    .filter((chat): chat is ApiChat => {
      if (!chat || chat.isRestricted) return false;
      if (isChatChannel(chat)) return true;
      // Include supergroups that function as broadcast channels (non-admins cannot send messages)
      if (isChatSuperGroup(chat) && chat.defaultBannedRights?.sendMessages) return true;
      return false;
    })
    .sort((a, b) => (lastMessageIds[b.id] || 0) - (lastMessageIds[a.id] || 0))
    .slice(0, FEED_CHANNEL_LIMIT)
    .map((chat) => chat.id);
}

let cachedAdminChannels: ApiChat[] = [];
let cachedAdminSignature = '';

/** Returns channel IDs where the user has admin rights to post messages. */
export function selectAdminChannels<T extends GlobalState>(global: T): ApiChat[] {
  const activeIds = global.chats.listIds.active || [];
  const chatsById = global.chats.byId;

  let signature = activeIds.length + ',';
  const newChannels: ApiChat[] = [];
  
  for (const id of activeIds) {
    const chat = chatsById[id];
    if (chat && !chat.isRestricted && chat.adminRights?.postMessages) {
      if (isChatChannel(chat) || (isChatSuperGroup(chat) && chat.defaultBannedRights?.sendMessages)) {
        newChannels.push(chat);
        signature += id + ',';
      }
    }
  }

  if (signature === cachedAdminSignature) {
    return cachedAdminChannels;
  }

  cachedAdminSignature = signature;
  cachedAdminChannels = newChannels;
  return cachedAdminChannels;
}

let cachedResult: { timeline: FeedItem[], buffer: FeedItem[] } = { timeline: [], buffer: [] };
let cachedSignature = '';

/**
 * Merges all loaded messages from subscribed channels into a single
 * reverse-chronological timeline. Pure — zero API calls.
 * Memoized using a signature of loaded message counts to prevent re-renders.
 */
export function selectFeedTimeline<T extends GlobalState>(global: T): { timeline: FeedItem[], buffer: FeedItem[] } {
  const channelIds = selectFeedChannelIds(global);
  const tabId = getCurrentTabId();
  const feedState = selectTabState(global, tabId)?.feed;
  const cutoffDate = feedState?.cutoffDate || 0;
  
  let signature = `cutoff:${cutoffDate},`;
  for (const chatId of channelIds) {
    const messagesById = global.messages.byChatId[chatId]?.byId;
    const count = messagesById ? Object.keys(messagesById).length : 0;
    signature += `${chatId}:${count},`;
  }

  if (signature === cachedSignature) {
    return cachedResult;
  }

  const now = Math.floor(Date.now() / 1000);
  const ageCutoff = now - FEED_POST_AGE_SECONDS;
  
  const timeline: FeedItem[] = [];
  const buffer: FeedItem[] = [];

  for (const chatId of channelIds) {
    const messagesById = global.messages.byChatId[chatId]?.byId;
    if (!messagesById) continue;

    for (const msg of Object.values(messagesById) as ApiMessage[]) {
      if (!msg.date || msg.date < ageCutoff) continue;
      // Skip replies in threads — only top-level channel posts
      if (msg.replyInfo?.type === 'message' && msg.replyInfo.replyToMsgId) continue;
      
      const item = { chatId, messageId: msg.id, date: msg.date };
      if (cutoffDate && msg.date > cutoffDate) {
        buffer.push(item);
      } else {
        timeline.push(item);
      }
    }
  }

  timeline.sort((a, b) => b.date - a.date);
  buffer.sort((a, b) => b.date - a.date);
  
  cachedSignature = signature;
  cachedResult = { timeline, buffer };
  
  return cachedResult;
}

export function selectFeedState<T extends GlobalState>(global: T, tabId?: number) {
  return selectTabState(global, tabId ?? getCurrentTabId()).feed;
}
